import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { gsap } from 'gsap';
import Experience from './Experience.js';

export default class Camera
{
    constructor()
    {
        this.experience = new Experience();
        this.sizes = this.experience.sizes;
        this.quality = this.experience.quality;
        this.scene = this.experience.scene;
        this.canvas = this.experience.canvas;
        this.debug = this.experience.debug;
        this.loadingScreen = this.experience.loadingScreen;

        // SceneDirector によるセクション連動tween（controls.target のみを動かす）
        this.sectionTween = null;

        // Signalscope の覗き込みモード（enterScopeMode / exitScopeMode で切替）
        this.isScopeMode = false;
        this.savedScope = null;

        // 3Dオブジェクトへのフォーカスモード（focusOn / clearFocus で切替）
        this.isFocusMode = false;
        this.savedFocus = null;

        // デバッグ用フリーカメラ（true の間はスクロール演出がカメラを一切動かさない）
        this.debugFreeCamera = false;

        // アニメーション用のプロパティ
        this.initialTarget = { x: 0, y: 30, z: 0 };  // 初期ターゲット（上の方）
        this.finalTarget = { x: 0, y: 0, z: 0 };    // 最終ターゲット（正面）
        this.animationState = {
            isAnimating: false,
            startTime: 0,
            duration: 5000, // 3秒でアニメーション
            progress: 0
        };

        if (this.debug.active)
        {
            this.debugFolder = this.debug.ui.addFolder('Camera');
            this.debugFolder.close();
        }

        // カメラの設定
        this.setInstance();
        this.setOrbitControls();

        // loadingScreen が閉じたら実行する処理
        this.loadingScreen.on('completed', () => {
            // 初期アニメーションを開始（少し遅延させる）
            // TODO: 1000 → 5000
            setTimeout(() => {
                this.startTargetAnimation();
            }, 1000);
        });
    }

    setInstance()
    {
        this.instance = new THREE.PerspectiveCamera(35, this.sizes.width / this.sizes.height, 0.1, 100);

        // カメラの初期位置を設定
        this.instance.position.set(0, 0, 30);
        // 注意：OrbitControls使用時は、rotationではなくpositionとtargetで向きを制御
        // this.instance.rotation.x = Math.PI / 2;

        this.scene.add(this.instance);

        // デバッグコントロール
        if (this.debug.active)
        {
            // カメラの位置をデバッグ
            this.debugFolder.add(this.instance.position, 'x')
                .min(-50).max(50).step(0.1).name('Position X');
            this.debugFolder.add(this.instance.position, 'y')
                .min(-50).max(50).step(0.1).name('Position Y');
            this.debugFolder.add(this.instance.position, 'z')
                .min(-50).max(50).step(0.1).name('Position Z');

            // カメラの回転をデバッグ（ここが今回の重要な部分！）
            this.debugFolder.add(this.instance.rotation, 'x')
                .min(-Math.PI).max(Math.PI).step(0.01).name('Rotation X');
            this.debugFolder.add(this.instance.rotation, 'y')
                .min(-Math.PI).max(Math.PI).step(0.01).name('Rotation Y');
            this.debugFolder.add(this.instance.rotation, 'z')
                .min(-Math.PI).max(Math.PI).step(0.01).name('Rotation Z');

            // カメラのFOV（視野角）もデバッグ
            this.debugFolder.add(this.instance, 'fov')
                .min(10).max(100).step(1).name('Field of View')
                .onChange(() => {
                    this.instance.updateProjectionMatrix();
                });
        }
    }

    setOrbitControls()
    {
        this.controls = new OrbitControls(this.instance, this.canvas);
        this.controls.enableDamping = true; // スムーズな動き

        // 初期ターゲットを上の方に設定
        this.controls.target.set(
            this.initialTarget.x,
            this.initialTarget.y,
            this.initialTarget.z
        );

        // TODO: 下記の設定もシーンのStateによって制限を更新したいね
        // === カメラの制限設定 ===

        // 距離制限（ズーム制限）
        this.controls.minDistance = 5;   // 最小距離（これより近づけない）
        this.controls.maxDistance = 50;  // 最大距離（これより遠ざかれない）

        // 縦方向の回転制限（上下の見上げ・見下ろし制限）
        this.controls.minPolarAngle = Math.PI * 0.1;  // 最小角度（上を見る制限）
        this.controls.maxPolarAngle = Math.PI * 0.9;  // 最大角度（下を見る制限）

        // 横方向の回転制限（左右の回転制限）
        // this.controls.minAzimuthAngle = -Math.PI / 2; // 左90度まで
        // this.controls.maxAzimuthAngle = Math.PI / 2;  // 右90度まで

        // パン（平行移動）制限
        this.controls.enablePan = true;
        this.controls.panSpeed = 1.0;

        this.controls.update(); // 設定を反映

        // ユーザーがドラッグを始めたら、進行中のセクションtweenは破棄する（ユーザーの意思を優先）
        this.controls.addEventListener('start', () => {
            if (this.sectionTween) {
                this.sectionTween.kill();
                this.sectionTween = null;
            }
        });

        // OrbitControlsのデバッグ設定
        if (this.debug.active)
        {
            const controlsFolder = this.debugFolder.addFolder('Orbit Controls');

            // フリーカメラ: フォーカス演出を無効化して自由に構図を探すモード
            this.debugFolder.add(this, 'debugFreeCamera').name('🎥 Free Camera (Debug)')
                .onChange((value) => this.setFreeCameraMode(value));
            this.debugFolder.add({ log: () => this.logCameraPose() }, 'log').name('📋 Log Camera Pose');

            // デバッグモード切り替え
            this.debugSettings = {
                manualMode: false, // 手動カメラ制御モード
                orbitEnabled: true // OrbitControls有効/無効
            };

            controlsFolder.add(this.debugSettings, 'manualMode')
                .name('Manual Camera Mode')
                .onChange((value) => {
                    this.controls.enabled = !value;
                    if (value) {
                        console.log('Manual camera mode: You can now adjust rotation manually');
                    } else {
                        console.log('OrbitControls mode: Camera controlled by mouse');
                    }
                });

            controlsFolder.add(this.controls, 'enabled').name('Enable OrbitControls');
            controlsFolder.add(this.controls, 'enableDamping').name('Enable Damping');
            controlsFolder.add(this.controls, 'dampingFactor')
                .min(0).max(1).step(0.01).name('Damping Factor');

            // 基本機能の制御（フォーカスモードが書き換えるため listen で実値に追従させる）
            controlsFolder.add(this.controls, 'enableZoom').name('Enable Zoom').listen();
            controlsFolder.add(this.controls, 'enableRotate').name('Enable Rotate').listen();
            controlsFolder.add(this.controls, 'enablePan').name('Enable Pan').listen();

            // === 制限設定のデバッグコントロール ===
            const limitsFolder = controlsFolder.addFolder('Camera Limits');

            // 距離制限
            limitsFolder.add(this.controls, 'minDistance')
                .min(0.1).max(20).step(0.1).name('Min Distance');
            limitsFolder.add(this.controls, 'maxDistance')
                .min(10).max(100).step(1).name('Max Distance');

            // 縦方向回転制限（PolarAngle = 上下の制限）
            limitsFolder.add(this.controls, 'minPolarAngle')
                .min(0).max(Math.PI).step(0.01).name('Min Polar (Up limit)');
            limitsFolder.add(this.controls, 'maxPolarAngle')
                .min(0).max(Math.PI).step(0.01).name('Max Polar (Down limit)');

            // 横方向回転制限（AzimuthAngle = 左右の制限）
            const azimuthControls = {
                enableAzimuthLimit: false,
                minAzimuth: -Math.PI / 2,
                maxAzimuth: Math.PI / 2
            };

            limitsFolder.add(azimuthControls, 'enableAzimuthLimit')
                .name('Enable Azimuth Limit')
                .onChange((value) => {
                    if (value) {
                        this.controls.minAzimuthAngle = azimuthControls.minAzimuth;
                        this.controls.maxAzimuthAngle = azimuthControls.maxAzimuth;
                    } else {
                        this.controls.minAzimuthAngle = -Infinity;
                        this.controls.maxAzimuthAngle = Infinity;
                    }
                });

            limitsFolder.add(azimuthControls, 'minAzimuth')
                .min(-Math.PI).max(Math.PI).step(0.01).name('Min Azimuth (Left)')
                .onChange((value) => {
                    if (azimuthControls.enableAzimuthLimit) {
                        this.controls.minAzimuthAngle = value;
                    }
                });

            limitsFolder.add(azimuthControls, 'maxAzimuth')
                .min(-Math.PI).max(Math.PI).step(0.01).name('Max Azimuth (Right)')
                .onChange((value) => {
                    if (azimuthControls.enableAzimuthLimit) {
                        this.controls.maxAzimuthAngle = value;
                    }
                });

            // === アニメーション制御のデバッグ ===
            const animationFolder = controlsFolder.addFolder('Target Animation');

            // アニメーション設定
            const animationSettings = {
                duration: this.animationState.duration,
                initialY: this.initialTarget.y,
                finalY: this.finalTarget.y,
                startAnimation: () => this.startTargetAnimation(),
                resetToInitial: () => this.resetTargetToInitial(),
                jumpToFinal: () => this.jumpToFinal()
            };

            animationFolder.add(animationSettings, 'duration')
                .min(500).max(10000).step(100).name('Animation Duration (ms)')
                .onChange((value) => {
                    this.animationState.duration = value;
                });

            animationFolder.add(animationSettings, 'initialY')
                .min(-20).max(30).step(0.5).name('Initial Target Y')
                .onChange((value) => {
                    this.initialTarget.y = value;
                });

            animationFolder.add(animationSettings, 'finalY')
                .min(-20).max(20).step(0.5).name('Final Target Y')
                .onChange((value) => {
                    this.finalTarget.y = value;
                });

            animationFolder.add(animationSettings, 'startAnimation').name('🎬 Start Animation');
            animationFolder.add(animationSettings, 'resetToInitial').name('⬆️ Reset to Initial');
            animationFolder.add(animationSettings, 'jumpToFinal').name('⬇️ Jump to Final');

            // 現在の状態表示
            const statusFolder = animationFolder.addFolder('Status');
            const status = {
                isAnimating: this.animationState.isAnimating,
                progress: '0%'
            };
            statusFolder.add(status, 'isAnimating').name('Is Animating').listen();
            statusFolder.add(status, 'progress').name('Progress').listen();

            // ステータス更新のためにstatusオブジェクトを保存
            this.debugStatus = status;

            // OrbitControlsのtarget設定（カメラが見る方向）
            const targetFolder = controlsFolder.addFolder('Camera Target');
            targetFolder.add(this.controls.target, 'x').min(-20).max(20).step(0.1).name('Target X');
            targetFolder.add(this.controls.target, 'y').min(-20).max(20).step(0.1).name('Target Y');
            targetFolder.add(this.controls.target, 'z').min(-20).max(20).step(0.1).name('Target Z');
        }
    }

    // === ターゲットアニメーション関数群 ===
    startTargetAnimation()
    {
        console.log('🎬 Starting camera target animation...');
        this.animationState.isAnimating = true;
        this.animationState.startTime = Date.now();
        this.animationState.progress = 0;

        // 初期位置にリセット
        this.controls.target.set(
            this.initialTarget.x,
            this.initialTarget.y,
            this.initialTarget.z
        );
        this.controls.update();
    }

    resetTargetToInitial()
    {
        console.log('⬆️ Resetting camera to initial position...');
        this.animationState.isAnimating = false;
        this.controls.target.set(
            this.initialTarget.x,
            this.initialTarget.y,
            this.initialTarget.z
        );
        this.controls.update();
    }

    jumpToFinal()
    {
        console.log('⬇️ Jumping camera to final position...');
        this.animationState.isAnimating = false;
        this.controls.target.set(
            this.finalTarget.x,
            this.finalTarget.y,
            this.finalTarget.z
        );
        this.controls.update();
    }

    /**
     * セクション切替時に視線（controls.target）だけをゆっくり動かす。
     * position や FOV には触れないので OrbitControls のユーザー操作と喧嘩しない
     * （イントロの updateTargetAnimation と同じ実証済みパターン）。
     */
    setSectionTarget(target, duration = 2.0)
    {
        // デバッグ用フリーカメラ中はスクロール演出にカメラを渡さない
        if (this.debugFreeCamera) return;

        // イントロアニメーション中は何もしない（SceneDirector 側で保留される）
        if (this.animationState.isAnimating) return;

        // スコープ覗き込み中は照準を奪わない（Signalscope 側が sectionChanged で exit する）
        if (this.isScopeMode) return;

        // フォーカス中は clearFocus 経由でのみ復帰する（SceneDirector が分岐済みだが念のため）
        if (this.isFocusMode) return;

        if (this.sectionTween) {
            this.sectionTween.kill();
            this.sectionTween = null;
        }

        // reduced-motion 時は即座に反映
        if (this.quality.reducedMotion || duration <= 0) {
            this.controls.target.set(target.x, target.y, target.z);
            this.controls.update();
            return;
        }

        this.sectionTween = gsap.to(this.controls.target, {
            x: target.x,
            y: target.y,
            z: target.z,
            duration: duration,
            ease: 'power2.inOut',
            overwrite: true
        });
    }

    /**
     * Signalscope の覗き込みモードへ入る。
     * カメラ position は動かさず FOV だけズームする。照準（controls.target）は
     * Signalscope が毎フレーム天体を自動追尾して書き込むため、ここでは触らない
     * （controls.update() が毎フレーム走るので、カメラを直接回すのではなく
     * controls.target 経由で照準する）。
     * 成功したら true。イントロアニメーション中は false を返して何もしない。
     */
    enterScopeMode(duration = 1.6)
    {
        if (this.isScopeMode) return false;
        if (this.animationState.isAnimating) return false;

        if (this.sectionTween) {
            this.sectionTween.kill();
            this.sectionTween = null;
        }

        this.isScopeMode = true;
        this.savedScope = {
            target: this.controls.target.clone(),
            minDistance: this.controls.minDistance,
            maxDistance: this.controls.maxDistance,
            enableZoom: this.controls.enableZoom,
            enablePan: this.controls.enablePan,
            enableRotate: this.controls.enableRotate,
            minPolarAngle: this.controls.minPolarAngle,
            maxPolarAngle: this.controls.maxPolarAngle,
            fov: this.instance.fov
        };

        // 照準は自動追尾なのでユーザー操作は全て無効化
        this.controls.minDistance = 0.1;
        this.controls.enableZoom = false;
        this.controls.enablePan = false;
        this.controls.enableRotate = false;
        this.controls.minPolarAngle = 0;
        this.controls.maxPolarAngle = Math.PI;

        gsap.killTweensOf(this.controls.target);
        gsap.killTweensOf(this.instance);
        gsap.to(this.instance, {
            fov: 16,
            duration: duration * this.quality.motionScale,
            ease: 'power2.inOut',
            onUpdate: () => this.instance.updateProjectionMatrix()
        });

        return true;
    }

    /** 覗き込みモードから戻る。tween 完了後に OrbitControls の制限を復元して onDone を呼ぶ */
    exitScopeMode(onDone, duration = 1.2)
    {
        if (!this.isScopeMode || !this.savedScope) {
            if (onDone) onDone();
            return;
        }

        const saved = this.savedScope;
        const tweenDuration = duration * this.quality.motionScale;

        gsap.killTweensOf(this.controls.target);
        gsap.to(this.controls.target, {
            x: saved.target.x, y: saved.target.y, z: saved.target.z,
            duration: tweenDuration, ease: 'power2.inOut', overwrite: true
        });
        gsap.killTweensOf(this.instance);
        gsap.to(this.instance, {
            fov: saved.fov,
            duration: tweenDuration, ease: 'power2.inOut',
            onUpdate: () => this.instance.updateProjectionMatrix(),
            onComplete: () => {
                this.controls.minDistance = saved.minDistance;
                this.controls.maxDistance = saved.maxDistance;
                this.controls.enableZoom = saved.enableZoom;
                this.controls.enablePan = saved.enablePan;
                this.controls.enableRotate = saved.enableRotate;
                this.controls.minPolarAngle = saved.minPolarAngle;
                this.controls.maxPolarAngle = saved.maxPolarAngle;
                this.isScopeMode = false;
                this.savedScope = null;
                if (onDone) onDone();
            }
        });
    }

    /**
     * カメラを指定姿勢へ寄せる（position + controls.target を同時 tween）。
     * scope と違い FOV は変えない。SceneDirector がセクション切替で呼び、
     * 3Dオブジェクト（本など）へ視点を寄せる汎用フォーカス機構。
     * 成功したら true。イントロ中・スコープ中は false を返して何もしない
     * （スコープを覗いたままフォーカスセクションへ来た場合はフォーカスをスキップし、
     * 次のセクション切替で回復する — 既知の許容制限）。
     */
    focusOn(cameraPos, lookTarget, { duration = 2.0 } = {})
    {
        if (this.debugFreeCamera) return false;
        if (this.animationState.isAnimating) return false;
        if (this.isScopeMode) return false;

        if (this.sectionTween) {
            this.sectionTween.kill();
            this.sectionTween = null;
        }
        gsap.killTweensOf(this.instance.position);
        gsap.killTweensOf(this.controls.target);

        // clearFocus のトランジション中に再フォーカスされた場合も、
        // 復帰先は最初にフォーカスした時点の姿勢を維持する
        if (!this.savedFocus) {
            this.savedFocus = {
                position: this.instance.position.clone(),
                minDistance: this.controls.minDistance,
                enableZoom: this.controls.enableZoom,
                enablePan: this.controls.enablePan,
                enableRotate: this.controls.enableRotate,
            };
        }
        this.isFocusMode = true;

        // minDistance = 5 のままだと controls.update() がカメラを外へ押し戻すため必ず緩める。
        // フォーカス中のユーザー操作は構図が壊れるので無効化（復帰時に戻す）
        this.controls.minDistance = 0.1;
        this.controls.enableZoom = false;
        this.controls.enablePan = false;
        this.controls.enableRotate = false;

        if (this.quality.reducedMotion || duration <= 0) {
            this.instance.position.copy(cameraPos);
            this.controls.target.copy(lookTarget);
            this.controls.update();
            return true;
        }

        gsap.to(this.instance.position, {
            x: cameraPos.x, y: cameraPos.y, z: cameraPos.z,
            duration: duration, ease: 'power2.inOut', overwrite: true
        });
        gsap.to(this.controls.target, {
            x: lookTarget.x, y: lookTarget.y, z: lookTarget.z,
            duration: duration, ease: 'power2.inOut', overwrite: true
        });
        return true;
    }

    /**
     * フォーカス解除。position はフォーカス前の位置へ、target は次セクションの視線へ tween。
     * OrbitControls の制限復元は position tween の完了後
     * （早く戻すと controls.update() にカメラが弾かれる — exitScopeMode と同じ手法）。
     */
    clearFocus(sectionTarget = null, { duration = 1.8 } = {})
    {
        if (this.debugFreeCamera) return;
        if (!this.isFocusMode || !this.savedFocus) return;

        this.isFocusMode = false;
        const saved = this.savedFocus;
        const target = sectionTarget ?? this.finalTarget;

        const restore = () => {
            this.controls.minDistance = saved.minDistance;
            this.controls.enableZoom = saved.enableZoom;
            this.controls.enablePan = saved.enablePan;
            this.controls.enableRotate = saved.enableRotate;
            this.savedFocus = null;
        };

        gsap.killTweensOf(this.instance.position);
        gsap.killTweensOf(this.controls.target);

        if (this.quality.reducedMotion || duration <= 0) {
            this.instance.position.copy(saved.position);
            this.controls.target.set(target.x, target.y, target.z);
            restore();
            this.controls.update();
            return;
        }

        gsap.to(this.controls.target, {
            x: target.x, y: target.y, z: target.z,
            duration: duration, ease: 'power2.inOut', overwrite: true
        });
        gsap.to(this.instance.position, {
            x: saved.position.x, y: saved.position.y, z: saved.position.z,
            duration: duration, ease: 'power2.inOut', overwrite: true,
            onComplete: restore
        });
    }

    /**
     * デバッグ用フリーカメラモードの切替。
     * ON の間は setSectionTarget / focusOn / clearFocus がすべて無効になり、
     * スクロールしても本の開閉・空の演出だけが進む（カメラは奪われない）。
     * 良い構図を探して「Log Camera Pose」で座標を書き出すためのモード。
     */
    setFreeCameraMode(enabled)
    {
        this.debugFreeCamera = enabled;

        // 2D UI 層（.portfolio-container）がポインターイベントを吸うと OrbitControls が
        // 操作できないため、UI 側（PortfolioUI）に切替を通知する（Experience → UI は DOM イベント経由）
        document.dispatchEvent(new CustomEvent('freeCameraChanged', { detail: { enabled } }));

        if (enabled) {
            // 進行中の演出 tween をすべて止めて、カメラを完全にユーザーへ渡す
            if (this.sectionTween) {
                this.sectionTween.kill();
                this.sectionTween = null;
            }
            gsap.killTweensOf(this.instance.position);
            gsap.killTweensOf(this.controls.target);

            // フォーカス状態は破棄（デバッグ専用なので元姿勢への復元はしない）
            this.isFocusMode = false;
            this.savedFocus = null;

            this.controls.enabled = true;
            this.controls.enableRotate = true;
            this.controls.enableZoom = true;
            this.controls.enablePan = true;
            this.controls.minDistance = 0.1;   // 本の近くまで寄れるように緩和
            this.controls.maxDistance = 100;
        } else {
            // 標準制約へ復元。カメラ位置はその場に残す
            //（次の sectionChanged か Scene Director の Apply Section で通常制御に復帰）。
            // ただし現在距離の外にある min/max を入れると controls.update() がカメラを
            // 弾き飛ばすため、現在距離を含む範囲までクランプする
            const currentDistance = this.instance.position.distanceTo(this.controls.target);
            this.controls.minDistance = Math.min(5, Math.max(0.1, currentDistance));
            this.controls.maxDistance = Math.max(50, currentDistance);
            this.controls.enableRotate = true;
            this.controls.enableZoom = true;
            this.controls.enablePan = true;
        }
    }

    /** 現在のカメラ姿勢を SceneDirector の設定にそのまま貼れる形式で console に出す */
    logCameraPose()
    {
        const round = (v) => Math.round(v * 1000) / 1000;
        const pose = {
            cameraPos: { x: round(this.instance.position.x), y: round(this.instance.position.y), z: round(this.instance.position.z) },
            target: { x: round(this.controls.target.x), y: round(this.controls.target.y), z: round(this.controls.target.z) }
        };
        console.log('📋 Camera Pose:', JSON.stringify(pose));
        return pose;
    }

    // アニメーションの更新処理
    updateTargetAnimation()
    {
        if (!this.animationState.isAnimating) return;

        const currentTime = Date.now();
        const elapsed = currentTime - this.animationState.startTime;
        let progress = elapsed / this.animationState.duration;

        // アニメーション完了チェック
        if (progress >= 1) {
            progress = 1;
            this.animationState.isAnimating = false;
            console.log('✅ Camera animation completed!');
        }

        // イージング関数（スムーズな動き）
        const easeProgress = this.easeInOutCubic(progress);

        // ターゲット位置を補間
        const currentTarget = {
            x: this.lerp(this.initialTarget.x, this.finalTarget.x, easeProgress),
            y: this.lerp(this.initialTarget.y, this.finalTarget.y, easeProgress),
            z: this.lerp(this.initialTarget.z, this.finalTarget.z, easeProgress)
        };

        // OrbitControlsのターゲットを更新
        this.controls.target.set(currentTarget.x, currentTarget.y, currentTarget.z);

        // デバッグ情報更新
        this.animationState.progress = progress;
        if (this.debugStatus) {
            this.debugStatus.isAnimating = this.animationState.isAnimating;
            this.debugStatus.progress = `${Math.round(progress * 100)}%`;
        }
    }

    // 線形補間関数
    lerp(start, end, progress)
    {
        return start + (end - start) * progress;
    }

    // イージング関数（スムーズな加速・減速）
    easeInOutCubic(t)
    {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    resize()
    {
        this.instance.aspect = this.sizes.width / this.sizes.height;
        this.instance.updateProjectionMatrix();
    }

    update()
    {
        // ターゲットアニメーションを更新
        this.updateTargetAnimation();

        // OrbitControlsを更新
        if (this.controls)
        {
            this.controls.update();
        }
    }
}